import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabaseClient'

export default function AdminUsers() {
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [search, setSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState('')
    const [selectedUser, setSelectedUser] = useState(null)

    const loadUsers = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            // Fetch all profiles
            const { data: profiles, error: err } = await supabase
                .from('profiles')
                .select('*')
                .order('created_at', { ascending: false })

            if (err) throw err

            // Fetch registrations to mix in extra data like license URL if needed
            const { data: regs, error: regsErr } = await supabase.from('registrations').select('*')
            if (regsErr) console.error(regsErr)

            const regMap = {}
            if (regs) {
                regs.forEach(r => {
                    if (r.email) regMap[r.email.toLowerCase()] = r
                })
            }

            const merged = profiles.map(p => ({
                ...p,
                registrations: p.email ? regMap[p.email.toLowerCase()] : null
            }))

            setUsers(merged)
        } catch (e) {
            console.error(e)
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadUsers()
    }, [loadUsers])

    const roleLabel = (role) => {
        if (role === 'pharmacist') return '약사'
        if (role === 'pharmacy') return '약국'
        if (role === 'admin') return '관리자'
        return role || '-'
    }

    const filteredUsers = users.filter(u => {
        const t = `${u.email || ''} ${u.display_name || ''} ${u.phone || ''}`.toLowerCase()
        const matchSearch = search ? t.includes(search.toLowerCase()) : true
        const matchRole = roleFilter ? u.role === roleFilter : true
        return matchSearch && matchRole
    })

    return (
        <div className="grid gap-6">
            <div>
                <h2 className="text-lg font-semibold mb-1">회원 관리</h2>
                <p className="text-xs text-gray-500">
                    서비스에 가입된 모든 회원을 조회하고 상세 정보를 확인할 수 있습니다.
                </p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center bg-white border rounded-lg px-3 py-2 text-xs shadow-sm">
                <div className="flex items-center gap-1">
                    <span className="text-gray-600">검색:</span>
                    <input
                        className="border rounded px-2 py-1 text-xs w-48"
                        placeholder="이메일 / 이름 / 전화번호"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-1">
                    <span className="text-gray-600">역할:</span>
                    <select
                        className="border rounded px-2 py-1 text-xs"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <option value="">전체</option>
                        <option value="pharmacist">약사</option>
                        <option value="pharmacy">약국</option>
                        <option value="admin">관리자</option>
                    </select>
                </div>
                <div className="ml-auto">
                    <button onClick={loadUsers} className="text-blue-600 hover:underline">새로고침</button>
                </div>
            </div>

            {/* Table */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[800px]">
                        <thead>
                            <tr className="border-b bg-gray-50/50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                <th className="px-6 py-3 text-left">가입일</th>
                                <th className="px-6 py-3 text-left">이메일</th>
                                <th className="px-6 py-3 text-left">이름</th>
                                <th className="px-6 py-3 text-left">전화번호</th>
                                <th className="px-6 py-3 text-left">역할</th>
                                <th className="px-6 py-3 text-left">상태</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && <tr><td colSpan="6" className="p-4 text-center">로딩 중...</td></tr>}
                            {!loading && filteredUsers.length === 0 && <tr><td colSpan="6" className="p-4 text-center text-gray-400">검색 결과가 없습니다.</td></tr>}
                            {filteredUsers.map(u => (
                                <tr
                                    key={u.id}
                                    className={`hover:bg-gray-50 cursor-pointer ${selectedUser?.id === u.id ? 'bg-blue-50' : ''}`}
                                    onClick={() => setSelectedUser(selectedUser?.id === u.id ? null : u)}
                                >
                                    <td className="px-6 py-4 text-gray-500 text-xs">
                                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                                    </td>
                                    <td className="px-6 py-4 font-medium text-gray-900">{u.email}</td>
                                    <td className="px-6 py-4 text-gray-600">{u.display_name || '-'}</td>
                                    <td className="px-6 py-4 text-gray-600">{u.phone || '-'}</td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium 
                              ${u.role === 'pharmacist' ? 'bg-purple-100 text-purple-800' :
                                                u.role === 'pharmacy' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {roleLabel(u.role)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${u.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                                                u.approval_status === 'rejected' ? 'bg-rose-100 text-rose-800' :
                                                    'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {u.approval_status === 'approved' ? '승인됨' :
                                                u.approval_status === 'rejected' ? '거절됨' : '대기중'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Selected User Detail */}
            {selectedUser && (
                <div className="rounded-2xl border border-blue-100 bg-white shadow-lg animate-fade-in-up ring-4 ring-blue-50/50 p-6">
                    <div className="flex justify-between items-start mb-6 border-b pb-4">
                        <div>
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                {selectedUser.display_name || '이름 없음'}
                                <span className="text-sm font-normal text-gray-500">({selectedUser.email})</span>
                            </h3>
                            <p className="text-xs text-gray-400 mt-1">User ID: {selectedUser.user_id || selectedUser.id}</p>
                        </div>
                        <button onClick={() => setSelectedUser(null)} className="text-gray-400 hover:text-gray-600">닫기</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-bold text-gray-900 mb-2">기본 정보</h4>
                            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-gray-500">전화번호</span> <span>{selectedUser.phone || '-'}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">역할</span> <span>{roleLabel(selectedUser.role)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">상태</span> <span>{selectedUser.approval_status}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">승인일</span> <span>{selectedUser.approved_at ? new Date(selectedUser.approved_at).toLocaleString() : '-'}</span></div>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900 mb-2">가입/면허 정보</h4>
                            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                                {/* Registration Data if available */}
                                {selectedUser.registrations && (
                                    <>
                                        <div className="flex justify-between"><span className="text-gray-500">면허번호</span> <span>{selectedUser.registrations.license_number || '-'}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">약국명</span> <span>{selectedUser.registrations.pharmacy_name || '-'}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">주소</span> <span className="text-right max-w-[200px]">{selectedUser.registrations.pharmacy_address || selectedUser.registrations.personal_address || '-'}</span></div>
                                    </>
                                )}
                                {!selectedUser.registrations && <p className="text-gray-400">상세 가입 정보 없음</p>}
                            </div>
                        </div>

                        {/* License Image */}
                        <div className="md:col-span-2">
                            <h4 className="font-bold text-gray-900 mb-2">제출 서류</h4>
                            <div className="bg-gray-50 p-4 rounded-lg flex justify-center">
                                {(selectedUser.registrations?.license_attachment_url) ? (
                                    <img
                                        src={selectedUser.registrations.license_attachment_url}
                                        alt="Licensed"
                                        className="max-w-full max-h-96 rounded shadow-sm object-contain"
                                    />
                                ) : (
                                    <span className="text-gray-400 text-sm">첨부된 이미지가 없습니다.</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}
